import { createSlice, Slice } from '@reduxjs/toolkit';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { ISentence, ISentenceListItem } from '../interfaces/sentence';

export const SENTENCE_KEY_IN_STORE = 'sentences';

export interface ISentencesState {
	isLoading: boolean;
	sentences: IPaginatedData<ISentenceListItem>;
	deleteSentence: IReqStatus;
	addSentence: IReqStatus;
	searchSentencesAutoComplete: {
		isLoading: boolean;
		result: ISentenceListItem[];
	};
	searchSentencesByPreviousLessonsAutoComplete: {
		isLoading: boolean;
		result: ISentenceListItem[];
	};
	updateSentence: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISentence;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: ISentencesState = {
	sentences: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	searchSentencesAutoComplete: {
		isLoading: false,
		result: [],
	},
	searchSentencesByPreviousLessonsAutoComplete: {
		isLoading: false,
		result: [],
	},
	isLoading: false,
	addSentence: {
		success: false,
		error: false,
	},
	deleteSentence: {
		success: false,
		error: false,
	},
	updateSentence: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
};

const sentenceSlice: Slice = createSlice({
	name: SENTENCE_KEY_IN_STORE,
	initialState,
	reducers: {
		resetSentenceData: () => {
			return initialState;
		},
		getSentences: state => {
			state.isLoading = true;
		},
		getSentencesSuccess: (state, action) => {
			state.sentences = action.payload;
			state.isLoading = false;
		},
		getSentencesFailure: state => {
			state.isLoading = false;
		},
		deleteSentence: state => {
			state.deleteSentence = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteSentenceSuccess: (state: RootStateOrAny) => {
			state.deleteSentence = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteSentenceFailure: (state: RootStateOrAny) => {
			state.deleteSentence = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteSentenceStatus: state => {
			state.deleteSentence = {
				success: false,
				error: false,
			};
		},
		addSentence: state => {
			state.isLoading = true;
			state.addSentence = {
				success: false,
				error: false,
			};
		},
		addSentenceSuccess: (state: RootStateOrAny) => {
			state.addSentence = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addSentenceFailure: state => {
			state.addSentence = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getSentenceToUpdate: state => {
			state.updateSentence.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSentenceToUpdateSuccess: (state, action) => {
			state.updateSentence.itemToUpdate = {
				isLoading: false,
				data: action.payload,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSentenceToUpdateFailure: state => {
			state.updateSentence.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSentence: state => {
			state.isLoading = true;
			state.updateSentence.status = {
				success: false,
				error: false,
			};
		},
		updateSentenceSuccess: state => {
			state.isLoading = false;
			state.updateSentence.status = {
				success: true,
				error: false,
			};
		},
		updateSentenceFailure: state => {
			state.isLoading = false;
			state.updateSentence.status = {
				success: false,
				error: true,
			};
		},

		searchSentences: state => {
			state.searchSentencesAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchSentencesAutoCompleteResult: (state, action) => {
			state.searchSentencesAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSetSearchSentencesAutoComplete: state => {
			state.searchSentencesAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		searchSentencesByPreviousLessons: state => {
			state.searchSentencesByPreviousLessonsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchSentencesByPreviousLessonsAutoComplete: (state, action) => {
			state.searchSentencesByPreviousLessonsAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSearchSentencesByPreviousLessonsAutoComplete: state => {
			state.searchSentencesByPreviousLessonsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},
	},
});

export const {
	getSentences,
	getSentencesSuccess,
	getSentencesFailure,

	deleteSentence,
	deleteSentenceSuccess,
	deleteSentenceFailure,

	resetDeleteSentenceStatus,
	addSentence,
	addSentenceSuccess,
	addSentenceFailure,
	resetSentenceData,

	getSentenceToUpdate,
	getSentenceToUpdateSuccess,
	getSentenceToUpdateFailure,

	updateSentence,
	updateSentenceSuccess,
	updateSentenceFailure,

	searchSentences,
	setSearchSentencesAutoCompleteResult,
	resetSetSearchSentencesAutoComplete,

	searchSentencesByPreviousLessons,
	setSearchSentencesByPreviousLessonsAutoComplete,
	resetSearchSentencesByPreviousLessonsAutoComplete,
} = sentenceSlice.actions;

export const sentenceStateSelector = (state: RootStateOrAny): ISentencesState =>
	state[SENTENCE_KEY_IN_STORE];

export default sentenceSlice;
